import { useEffect, useMemo, useState } from 'react';

import { VehicleApi } from '@/api';
import { useFormConfigLoader } from '@/hooks/useFormConfigLoader';
import { IFormSelectItem, IOffenseOptionItem } from '@/models';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';
import { useDataStore, useToastStore } from '@/store';

const vehicleMinYear = 1935;

export const useMasterVehicleFormOptions = (
  vehicleType?: string,
  makeName?: string,
  modelName?: string,
  selectedYear?: number | string,
) => {
  const { updateToast } = useToastStore();
  const { filteredOptions: VehicleTypeOptions } = useFormConfigLoader({
    optionsName: 'PROPERTY',
    category: 'PROPERTY',
  });

  const VehicleTypeList = getOffenseOptionItems(
    'VEHICLE_PROPERTY_TYPES',
    VehicleTypeOptions,
  ) as Map<string, IOffenseOptionItem>;

  const makes = useDataStore((state) =>
    VehicleTypeList?.size &&
    vehicleType === VehicleTypeList?.get('AUTOMOBILES')?.key
      ? state.makes
      : [],
  );

  const [modelOptions, setModelOptions] = useState<IFormSelectItem[]>([]);
  const [colorOptions, setColorOptions] = useState<IFormSelectItem[]>([]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const diffYears = currentYear - vehicleMinYear;
    const options: IFormSelectItem[] = [];

    for (let i = 0; i <= diffYears; i++) {
      const yearValue = vehicleMinYear + i;
      options.unshift({
        label: `${yearValue}`,
        value: `${yearValue}`,
      });
    }
    return options;
  }, []);
  const [licensePlateStateOptions, setLicensePlateStateOptions] = useState<
    IFormSelectItem[]
  >([]);
  const [licensePlateTypeOptions, setLicensePlateTypeOptions] = useState<
    IFormSelectItem[]
  >([]);

  const makeOptions = useMemo(
    () =>
      makes.map(({ _id, name }) => ({
        label: name,
        value: name,
        _id,
      })),
    [makes],
  );
  useEffect(() => {
    fetchLicensePlateStates();
    fetchLicensePlateTypes();
  }, [vehicleType]);

  const fetchLicensePlateStates = async () => {
    try {
      const res = await VehicleApi.getVehicleLicenseStates({
        limit: 1000,
      });
      setLicensePlateStateOptions(
        res.data.map((state) => ({
          label: `${state.code} - ${state.name}`,
          value: state.code,
        })),
      );
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const fetchLicensePlateTypes = async () => {
    try {
      const res = await VehicleApi.getVehicleLicenseTypes({
        limit: 1000,
      });
      setLicensePlateTypeOptions(
        res.data.map((type) => ({
          label: `${type.code} - ${type.description}`,
          value: type.code,
        })),
      );
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  useEffect(() => {
    const shouldFetchModels =
      VehicleTypeList?.size &&
      vehicleType === VehicleTypeList?.get('AUTOMOBILES')?.key &&
      makeName &&
      getMakeIdByName(makeName);

    if (shouldFetchModels) {
      fetchModels();
    } else {
      setModelOptions([]);
      setColorOptions([]);
    }
  }, [selectedYear, makeName, vehicleType, makes]);

  useEffect(() => {
    if (
      VehicleTypeList?.size &&
      vehicleType !== VehicleTypeList?.get('AUTOMOBILES')?.key
    ) {
      return;
    }
    if (modelName && selectedYear && makeName) {
      const makeItemId = getMakeIdByName(makeName);
      const modelItemId = getModelIdByName(modelName);

      if (makeItemId && modelItemId) {
        fetchColors();
      } else {
        setColorOptions([]);
      }
    } else {
      setColorOptions([]);
    }
  }, [modelOptions, modelName, selectedYear, makeName, vehicleType, makes]);

  const getMakeIdByName = (makeNameValue?: string) => {
    if (!makeNameValue) {
      return null;
    }
    const make = makes.find((option) => option.name === makeNameValue);
    return make ? make._id : null;
  };

  const fetchModels = () => {
    const makeItemId = getMakeIdByName(makeName);
    if (!makeItemId) {
      return;
    }
    const yearToUse = selectedYear || new Date().getFullYear();

    VehicleApi.getVehicleModels(String(makeItemId), Number(yearToUse))
      .then((res) => {
        setModelOptions(
          res.data.map(({ name, _id }) => ({
            label: name,
            value: name,
            _id,
          })),
        );
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
      });
  };

  const getModelIdByName = (modelNameValue?: string) => {
    if (!modelNameValue) {
      return null;
    }
    const model = modelOptions?.find(
      (option) => option.value === modelNameValue,
    );
    return model ? (model as { _id: string })._id : null;
  };

  const fetchColors = async () => {
    try {
      const makeItemId = getMakeIdByName(makeName);
      if (!makeItemId) {
        setColorOptions([]);
        return;
      }
      const modelItemId = getModelIdByName(modelName);
      if (!modelItemId) {
        setColorOptions([]);
        return;
      }
      let filter = {};
      if (makeItemId && modelItemId && selectedYear) {
        filter = {
          make: makeItemId,
          model: modelItemId,
          year: selectedYear,
        };
        const res = await VehicleApi.getVehicleColors({
          limit: 1000,
          filter: JSON.stringify(filter),
        });
        setColorOptions(
          res.data.results.map(({ name }) => ({
            label: name,
            value: name,
          })),
        );
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  return {
    makeOptions,
    modelOptions,
    yearOptions,
    colorOptions,
    licensePlateStateOptions,
    licensePlateTypeOptions,
  };
};
